import { lineTotal, round2 } from './payment-lines.util';

/**
 * Resolving a booking's extra-item links into the add-ons the customer has
 * bought.
 *
 * The links are the server-side record of the purchase, and the only one this
 * app can read back after the payment redirect. There is no pre-basket row to
 * find: `bl_purchase_logs_*` is keyed by basket id + service item id and
 * belongs to the public apps' book-after-payment flow, whereas the portal is
 * book-first — the booking and purchase already exist and no basket is ever
 * built, so `/payment/hey-centric` is called without one.
 *
 * Shared by the My add-ons page and the add-ons confirmation page so both agree
 * on which links are add-ons and which are fees.
 */

/**
 * Item descriptions that live on a booking's extra-item links but are fees,
 * not add-ons. Matched on the service extra's `item_description` (resolved by
 * `item_id`) because that is the reliable identifier — a link's own
 * `data.item` is a free-text label that can drift.
 */
export const EXCLUDED_FEE_DESCRIPTIONS = [
  'Reschedule fee',
  'Booking fee',
  'Room Hire fee',
  'Ceremony fee',
];

/** The flat First-toast fee's item_description on the service extras. */
export const FIRST_TOAST_DESCRIPTION = 'First toast';

export interface PurchasedAddOn {
  /** The link's own label, as shown to the customer. */
  item: string;
  quantity: number;
  /** Line total (cost × quantity) in pounds. */
  total: number;
  /** The service extra's item_description — blank when it can't be resolved. */
  description: string;
}

/**
 * The add-ons on a booking, from its extra-item links resolved against the
 * service's extras. Fee lines and items already included in the ceremony price
 * are dropped. Links that resolve to nothing are kept: an unresolvable link is
 * still something the customer bought, and dropping it would silently hide it.
 */
export function resolvePurchasedAddOns(
  links: unknown,
  serviceExtras: unknown,
): PurchasedAddOn[] {
  const linkList = Array.isArray(links) ? links : [];
  const extras = Array.isArray(serviceExtras) ? serviceExtras : [];

  return linkList
    .map((link: any) => {
      const match = extras.find((extra: any) => extra?.item_id === link?.item_id);
      return {
        item: String(link?.data?.item ?? ''),
        quantity: Number(link?.data?.quantity) || 1,
        total: lineTotal(link?.data),
        description: String(match?.item_description ?? ''),
      };
    })
    .filter(
      (addOn) =>
        !addOn.description.includes('(Included)') &&
        !EXCLUDED_FEE_DESCRIPTIONS.includes(addOn.description),
    );
}

/** Total of the given add-on lines, in pounds. */
export function totalPurchasedAddOns(addOns: PurchasedAddOn[]): number {
  return round2((addOns ?? []).reduce((sum, addOn) => sum + addOn.total, 0));
}

/**
 * Whether a First toast is among the add-ons. Matched on the resolved
 * item_description rather than the link's free-text label.
 */
export function includesFirstToast(addOns: PurchasedAddOn[]): boolean {
  return (addOns ?? []).some(
    (addOn) => addOn.description === FIRST_TOAST_DESCRIPTION,
  );
}
